import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import LoginButton from '../NavButtons/LoginButton';
import RegisterButton from '../NavButtons/RegisterButton';
import AccountButton from '../NavButtons/AccountButton';
import ScrollableTabsButtonForce from './TabBar';
import { fade } from '@material-ui/core/styles/colorManipulator';

const DESKTOP_BREAKPOINT = 900;

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
    },
    appBar: props => ({
        backgroundColor: props.color,
        borderBottom: `1px solid ${fade(theme.palette.getContrastText(props.color), 0.12)}`,
        boxShadow: `0 12px 28px ${fade('#0f172a', 0.25)}`,
    }),
    toolbar: {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        '@media (min-width:900px)': {
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            gap: theme.spacing(2),
            padding: theme.spacing(1, 2),
        },
    },
    brand: {
        display: 'inline-flex',
        alignItems: 'baseline',
        textDecoration: 'none',
        letterSpacing: '-0.02em',
        fontFamily: '"Poppins", "Nunito", "Segoe UI", sans-serif',
        fontWeight: 800,
        whiteSpace: 'nowrap',
        flex: '0 0 auto',
        width: 'auto',
        justifyContent: 'flex-start',
        fontSize: '1rem',
    },
    brandTrivia: props => ({
        color: props.contrastText,
        textShadow: `0 3px 10px ${fade('#0f172a', 0.3)}`,
    }),
    brandWeekend: props => ({
        color: fade(props.contrastText, 0.82),
        textShadow: `0 3px 10px ${fade('#0f172a', 0.2)}`,
        position: 'relative',
    }),
    sparkle: props => ({
        marginLeft: theme.spacing(0.5),
        fontSize: '0.9em',
        color: fade(props.contrastText, 0.95),
        textShadow: `0 0 10px ${fade('#ffffff', 0.7)}, 0 0 16px ${fade('#ffe66d', 0.6)}`,
        transform: 'translateY(-2px) rotate(8deg)',
        display: 'none',
        '@media (min-width:900px)': {
            display: 'inline-block',
        },
    }),
    tabs: {
        display: 'flex',
        justifyContent: 'center',
        minWidth: 0,
        width: '100%',
        flexBasis: '100%',
        order: 3,
        marginTop: theme.spacing(0.25),
        overflowX: 'auto',
        '@media (min-width:900px)': {
            flex: '1 1 auto',
            width: 'auto',
            flexBasis: 'auto',
            order: 2,
            marginTop: 0,
            overflowX: 'visible',
        },
    },
    auth: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: '0 0 auto',
        order: 2,
        minWidth: 0,
        '@media (min-width:900px)': {
            marginLeft: theme.spacing(2),
            order: 3,
        },
    },
}));

const ButtonAppBar = ({ color, user }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(`(max-width:${DESKTOP_BREAKPOINT - 1}px)`);
    const baseColor = color || theme.palette.primary.main;
    const contrastText = theme.palette.getContrastText(baseColor);
    const classes = useStyles({ color: baseColor, contrastText });

    return (
        <div className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                    <Typography
                        variant="h6"
                        component={Link}
                        to="/"
                        className={classes.brand}
                        aria-label="triviaweekend home"
                    >
                        <Box component="span" className={classes.brandTrivia}>
                            trivia
                        </Box>
                        <Box component="span" className={classes.brandWeekend}>
                            weekend
                        </Box>
                        <Box component="span" className={classes.sparkle} aria-hidden="true">
                            ✨
                        </Box>
                    </Typography>
                    <Box className={classes.auth}>
                        {user.first_name ? (
                            <AccountButton firstName={user.first_name} compact={isMobile} />
                        ) : (
                            <>
                                <LoginButton color={color} />
                                <RegisterButton color={color} />
                            </>
                        )}
                    </Box>
                    <Box className={classes.tabs}>
                        <ScrollableTabsButtonForce selectedColor={color} />
                    </Box>
                </Toolbar>
            </AppBar>
        </div>
    );
}

const mapStateToProps = state => ({
    user: state.user,
});

export default connect(mapStateToProps)(ButtonAppBar)
